import moment from 'moment';
import { sortBy } from 'lodash';
import Highcharts from 'highcharts';
import { GlobalToken } from 'antd';
import { buildEngineStateMarkerSvg } from '@/comp/common/icons/EngineStateMarker';

interface EngineStatePoint {
  x: number;
  startDate: string;
  state: string;
  color: string;
}

interface BuildEngineStateOptions {
  engineStateChangePoints: EngineStatePoint[];
  showEngineStatus: boolean;
  getXAxisStart: () => number | null;
  getXAxisEnd: () => number | null;
  token: GlobalToken;
  t: (key: string) => string;
}

export const toRgba = (colorValue: string, opacity: number): string => {
  if (!colorValue) return colorValue;
  try {
    return Highcharts.color(colorValue).setOpacity(opacity).get('rgba') as string;
  } catch {
    return colorValue;
  }
};

export const buildEngineStatePlotBands = ({
  engineStateChangePoints,
  showEngineStatus,
  getXAxisStart,
  getXAxisEnd,
  token,
}: Omit<BuildEngineStateOptions, 't'>) => {
  if (!showEngineStatus) return [];
  if (!engineStateChangePoints?.length) return [];

  const chartEnd = getXAxisEnd();
  if (!chartEnd) return [];

  const sortedPoints = sortBy(engineStateChangePoints, ['x']);
  const chartStart = getXAxisStart();

  if (chartStart === null) return [];

  return sortedPoints
    .map((p, idx) => {
      const actualStartTime = moment(p.startDate).utc().valueOf();

      if (actualStartTime > chartEnd) return null;

      const from = Math.max(actualStartTime, chartStart);

      const nextPoint = sortedPoints?.[idx + 1];
      const nextPointTime = nextPoint ? moment(nextPoint.startDate).utc().valueOf() : chartEnd;
      const to = Math.min(nextPointTime, chartEnd);

      if (!from || !to || to <= from) return null;

      return {
        from,
        to,
        borderWidth: 0,
        zIndex: 0,
        color: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [
            [0, toRgba(p.color, 0.25)],
            [1, toRgba(token.colorBgContainer, 0)],
          ],
        },
      };
    })
    .filter(Boolean);
};

export const buildEngineStatePlotLines = ({
  engineStateChangePoints,
  showEngineStatus,
  getXAxisStart,
  getXAxisEnd,
  token,
  t,
}: BuildEngineStateOptions) => {
  if (!showEngineStatus) return [];
  if (!engineStateChangePoints?.length) return [];

  const sortedPoints = sortBy(engineStateChangePoints, ['x']);
  const chartStart = getXAxisStart();

  if (chartStart === null) return [];

  const chartEnd = getXAxisEnd();

  return sortedPoints
    .filter(p => {
      const actualStartTime = moment(p.startDate).utc().valueOf();
      return actualStartTime >= chartStart && (chartEnd === null || actualStartTime <= chartEnd);
    })
    .map((p, index) => {
      const actualStartTime = moment(p.startDate).utc().valueOf();
      const dotPosition = Math.max(actualStartTime, chartStart);

      const dateTimestamp = moment(p.startDate).valueOf().toString();
      const tooltipData = JSON.stringify({
        status: t(p.state),
        date: dateTimestamp,
        color: p.color,
      });

      const safeTooltipData = btoa(encodeURIComponent(tooltipData));

      return {
        value: dotPosition,
        width: 1,
        color: 'transparent',
        id: `engine-state-line-${index}`,
        label: {
          useHTML: true,
          text: `<div class="engine-state-marker" data-tooltip="${safeTooltipData}">${buildEngineStateMarkerSvg(
            p.color,
            token.colorBgContainer,
            token.colorWarningBorder,
          )}</div>`,
          align: 'center',
          verticalAlign: 'center',
          rotation: 0,
          y: -6,
          x: 0,
          style: {
            pointerEvents: 'auto',
          },
        },
      };
    });
};
